import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { IconWrapper, SvgIcon } from '../Icon';
import { DimmerLoader } from '../Loaders';
import DevPermissionControl from '../../Layout/Header/DevPermissionControl';
import Syndicate from '../../pages/Auth/utils/Syndicate';
import { assertNotNil } from '../../utils/types';
import useLogout from '../../pages/Auth/hooks/useLogout';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { IS_LOCALHOST } from '../../config/constants';
import * as UserDropdownUI from './UserDropdownUI';
import useUser from '../../utils/hooks/useUser';
import useShowAdminSettings from '../../utils/permissions/useShowAdminSettings';
import { appRoutes } from '../../path';
import * as HeaderUI from '../../Layout/Header/HeaderUI';
import Avatar from '../Avatar';
import ThinDivider from '../ThinDivider';
import DarkModeToggle from './DarkModeToggle';
import { FloatingLayer } from '../FloatingManager';

const UserDropdown = () => {
  const [isLogout, setIsLogout] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const user = useUser();
  const handleLogout = useLogout();

  const { logo: syndicateLogo } = Syndicate.getData();

  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);

  const canDisplayPermissionControl =
    permissionConfig.enablePermissionControl && IS_LOCALHOST;

  const showAdminSettings = useShowAdminSettings();

  const { floatingStyles, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(5)],
    placement: 'bottom-end',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const onClickLogout = () => {
    setIsLogout(true);
    handleLogout();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (isLogout) {
    return <DimmerLoader />;
  }

  return (
    <>
      <UserDropdownUI.IconContainer
        ref={refs.setReference}
        {...getReferenceProps()}
        $isActive={isOpen}
      >
        <UserDropdownUI.Logo src={syndicateLogo} />
        <IconWrapper size={24}>
          <SvgIcon
            name="caret-down-fill"
            width={12}
            height={12}
            customFill="currentColor"
          />
        </IconWrapper>
      </UserDropdownUI.IconContainer>

      {isOpen && (
        <FloatingLayer>
          <HeaderUI.Menu
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {user && (
              <UserDropdownUI.UserContainer>
                <Avatar picture={user?.picture} size={40} />
                <UserDropdownUI.UserInfo>
                  <UserDropdownUI.UserName>{user.name}</UserDropdownUI.UserName>
                  <UserDropdownUI.UserEmail>
                    {user.email}
                  </UserDropdownUI.UserEmail>
                </UserDropdownUI.UserInfo>
              </UserDropdownUI.UserContainer>
            )}
            {canDisplayPermissionControl && (
              <UserDropdownUI.UserContainer>
                <DevPermissionControl />
              </UserDropdownUI.UserContainer>
            )}
            <ThinDivider layout="horizontal" size="full" />
            <DarkModeToggle />
            <ThinDivider layout="horizontal" size="full" />
            <UserDropdownUI.MenuItem
              as={Link}
              to={appRoutes.settings.preferences}
              onClick={handleClose}
            >
              <SvgIcon
                name="user-gear"
                width={16}
                height={16}
                customFill="currentColor"
              />
              Personal Settings
            </UserDropdownUI.MenuItem>
            {showAdminSettings && (
              <UserDropdownUI.MenuItem
                as={Link}
                to={appRoutes.permission.allUsers}
                onClick={handleClose}
              >
                <SvgIcon
                  name="gear"
                  width={16}
                  height={16}
                  customFill="currentColor"
                />
                Admin Settings
              </UserDropdownUI.MenuItem>
            )}
            <UserDropdownUI.MenuItem onClick={onClickLogout}>
              <SvgIcon
                name="sign-out"
                width={16}
                height={16}
                customFill="currentColor"
              />
              Log Out
            </UserDropdownUI.MenuItem>
          </HeaderUI.Menu>
        </FloatingLayer>
      )}
    </>
  );
};

export default UserDropdown;
